import React, { memo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { MessageSquare, Pin } from 'lucide-react';
import { toast } from 'sonner';
import { contactsAPI } from '../../api';

const CreateNoteDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  onSuccess 
}) => {
  const [form, setForm] = useState({
    content: '',
    is_pinned: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.content.trim()) {
      toast.error('Please enter note content');
      return;
    }

    try {
      setLoading(true);
      
      await contactsAPI.createNote(contactId, {
        content: form.content,
        is_pinned: form.is_pinned
      });
      
      toast.success('Note added');
      setForm({ content: '', is_pinned: false });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Failed to create note:', error);
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setLoading(false);
    }
  }, [form, contactId, onOpenChange, onSuccess]);

  const handleClose = useCallback(() => {
    setForm({ content: '', is_pinned: false });
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-amber-600" />
            Add Note
          </DialogTitle>
          <DialogDescription>
            Add a note to this contact&apos;s profile
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="note-content">Note *</Label>
            <Textarea
              id="note-content"
              value={form.content}
              onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Enter your note..."
              rows={5}
              data-testid="note-content-input"
              autoFocus
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Checkbox 
              id="pin-note"
              checked={form.is_pinned}
              onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_pinned: checked }))}
            />
            <Label htmlFor="pin-note" className="text-sm flex items-center gap-1 cursor-pointer">
              <Pin className="h-3 w-3" />
              Pin this note
            </Label>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !form.content.trim()} data-testid="create-note-submit">
              {loading ? 'Adding...' : 'Add Note'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});

CreateNoteDialog.displayName = 'CreateNoteDialog';

export default CreateNoteDialog;
